import { useSearchParams } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import { useState } from 'react'
import DashboardHeader from './DashboardHeader'
import DashboardTabs from './DashboardTabs'
import SummaryTab from './SummaryTab'
import { PageScroll } from 'app/components/layout/PageScroll'
import { DateRangeCompareOptions, DateRangeOptions } from 'app/components/marketing/MarketingDateRangeSelector'
import SimpleTable from 'app/components/table/SimpleTable'
import { StatsCardType } from 'types/StatsCardType'

const EMPTY_COLUMNS: ColumnDef<never, any>[] = []
const EMPTY_COMPARE_SELECTOR_BLACKLIST: DateRangeCompareOptions[] = []

export default function TwoTabDashboard<T>({
  title,
  defaultColumns = EMPTY_COLUMNS as ColumnDef<T, any>[],
  filteredMetrics,
  dateChangeHandler,
  graph,
  stats,
  limitDateRangeToMonth = true,
  hideCompare = false,
  defaultDateRange,
  allowFutureDates = false,
  defaultChannel,
  compareSelectorBlacklist = EMPTY_COMPARE_SELECTOR_BLACKLIST,
  defaultCompareSelectorPreset = `previous_year`,
  graphTabLabel = `Summary`,
  tableTabLabel = `Location Breakdown`,
  handleFiltersChange,
  hideFooter = true,
  onSearchValueChange,
  mobileGraphReplacement,
}: {
  title: string
  defaultColumns: ColumnDef<T, any>[]
  filteredMetrics?: T[]
  dateChangeHandler: (
    fromDate: string,
    toDate: string,
    compareFromDate: string,
    compareToDate: string,
    comparingGoals: boolean,
    dontCompare: boolean,
  ) => void
  graph: React.ReactElement
  stats: StatsCardType[]
  limitDateRangeToMonth?: boolean
  hideCompare?: boolean
  defaultDateRange?: DateRangeOptions
  allowFutureDates?: boolean
  defaultChannel?: string | null
  compareSelectorBlacklist?: DateRangeCompareOptions[]
  defaultCompareSelectorPreset?: DateRangeCompareOptions
  graphTabLabel?: string
  tableTabLabel?: string
  handleFiltersChange?: (string) => void
  hideFooter?: boolean
  onSearchValueChange?: (searchValue: string) => void
  /** Mobile only: shown in place of the dropped time-series chart. */
  mobileGraphReplacement?: React.ReactElement
}) {
  const [searchParams] = useSearchParams()
  const [openTab, setOpenTab] = useState<string>(
    searchParams.has(`tab`) && [graphTabLabel, tableTabLabel].includes(searchParams.get(`tab`)!)
      ? searchParams.get(`tab`)!
      : graphTabLabel,
  )

  const handleTabChange = (tab: string) => {
    setOpenTab(tab)
    let url = new URL(window.location.href)
    url.searchParams.set(`tab`, tab)
    history.replaceState({}, ``, url.toString())
  }

  return (
    <PageScroll
      contentClassName="flex h-full flex-col pt-8 pb-14 md:pb-0"
      header={
        <>
          <DashboardHeader
            title={title}
            defaultChannel={defaultChannel}
            dateChangeHandler={dateChangeHandler}
            limitDateRangeToMonth={limitDateRangeToMonth}
            hideCompare={hideCompare}
            defaultDateRange={defaultDateRange}
            allowFutureDates={allowFutureDates}
            compareSelectorBlacklist={compareSelectorBlacklist}
            defaultCompareSelectorPreset={defaultCompareSelectorPreset}
            onFiltersChange={handleFiltersChange}
          />
          <DashboardTabs openTab={openTab} onSetOpenTab={handleTabChange} tabOptions={[graphTabLabel, tableTabLabel]} />
        </>
      }
    >
      {openTab === graphTabLabel ? (
        // Drop the time-series chart on mobile (reads poorly at phone width); the
        // stat cards carry the numbers. Matches the Marketing dashboard.
        <SummaryTab graph={graph} statsCards={stats} hideChartOnMobile mobileReplacement={mobileGraphReplacement} />
      ) : (
        <SimpleTable
          data={filteredMetrics}
          columns={defaultColumns}
          activeSubpage={openTab}
          showColumnOrdering
          enableExport
          hideFooter={hideFooter}
          onSearchValueChange={onSearchValueChange}
        />
      )}
    </PageScroll>
  )
}
